// ============================================================
// AttendanceCorrectionPage — 補打卡申請
// ============================================================
// 顯示員工最近 14 天「漏打卡」清單，可逐筆補登
// 提交後狀態 PENDING，需主管簽核
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { ErrorBanner } from './ErrorBanner';

interface MissingIssue {
  scheduleId: string;
  date: string; // YYYY-MM-DD
  startTime: string;
  endTime: string;
  type: 'NO_CLOCK_IN' | 'NO_CLOCK_OUT';
  clockIn?: string; // ISO，僅 NO_CLOCK_OUT 有
}

export function AttendanceCorrectionPage() {
  const [issues, setIssues] = useState<MissingIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<MissingIssue | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get<{ issues: MissingIssue[] }>('/attendance-correction/missing');
      setIssues(r.issues);
      setError(null);
    } catch (err) {
      // 不可吞掉：失敗時會顯示「✓ 沒有漏打卡紀錄」，把該補的卡藏起來。
      setError((err as Error).message);
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
      {error && <ErrorBanner message={error} onRetry={fetchData} />}

      <div>
        <h1 className="text-xl font-bold text-morandi-800">補打卡申請</h1>
        <p className="text-xs text-morandi-400 mt-1">
          列出最近 14 天「有排班但漏打卡」的紀錄，補登後送主管簽核
        </p>
      </div>

      {loading ? (
        <div className="text-center py-10 text-morandi-400">載入中...</div>
      ) : issues.length === 0 ? (
        <div className="card-morandi p-8 text-center">
          <div className="text-3xl mb-2">✓</div>
          <div className="text-sm text-morandi-600 font-medium">沒有漏打卡紀錄</div>
          <div className="text-xs text-morandi-400 mt-1">最近 14 天打卡都正常</div>
        </div>
      ) : (
        <div className="space-y-2">
          {issues.map(i => (
            <div key={i.scheduleId} className="card-morandi p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-morandi-700">{i.date}</span>
                    <span className="text-xs text-morandi-400 font-mono">
                      {i.startTime} – {i.endTime}
                    </span>
                  </div>
                  <div className={`badge text-xs mt-1 ${
                    i.type === 'NO_CLOCK_IN'
                      ? 'bg-rose-100 text-rose-600'
                      : 'bg-amber-100 text-amber-600'
                  }`}>
                    {i.type === 'NO_CLOCK_IN' ? '漏打上下班卡' : '漏打下班卡'}
                  </div>
                </div>
                <button
                  onClick={() => setEditing(i)}
                  className="px-3 py-1.5 bg-earth-400 hover:bg-earth-500 text-white rounded-btn text-sm shrink-0"
                >
                  補登
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <CorrectionModal
          issue={editing}
          onClose={() => setEditing(null)}
          onSubmitted={() => { setEditing(null); fetchData(); }}
        />
      )}
    </div>
  );
}

function CorrectionModal({ issue, onClose, onSubmitted }: {
  issue: MissingIssue;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const isFullMissing = issue.type === 'NO_CLOCK_IN';
  const [clockIn, setClockIn] = useState(`${issue.date}T${issue.startTime}`);
  const [clockOut, setClockOut] = useState(`${issue.date}T${issue.endTime}`);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (!reason.trim()) {
      setErr('請填補登原因');
      return;
    }
    setErr(null);
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        scheduleId: issue.scheduleId,
        reason: reason.trim(),
      };
      if (isFullMissing) {
        body.clockIn = new Date(clockIn).toISOString();
        body.clockOut = new Date(clockOut).toISOString();
      } else {
        body.clockOut = new Date(clockOut).toISOString();
      }
      await api.post('/attendance-correction', body);
      alert('已送出，請等主管簽核');
      onSubmitted();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div>
          <h2 className="text-lg font-bold text-morandi-800">補登 {issue.date} 打卡</h2>
          <p className="text-xs text-morandi-400 mt-0.5">
            排班 {issue.startTime}–{issue.endTime} · {isFullMissing ? '漏打上下班卡' : '漏打下班卡'}
          </p>
        </div>

        {isFullMissing && (
          <div>
            <label className="text-sm text-morandi-500 block mb-1">實際上班時間</label>
            <input
              type="datetime-local"
              value={clockIn}
              onChange={e => setClockIn(e.target.value)}
              className="input-field"
            />
          </div>
        )}

        <div>
          <label className="text-sm text-morandi-500 block mb-1">實際下班時間</label>
          <input
            type="datetime-local"
            value={clockOut}
            onChange={e => setClockOut(e.target.value)}
            className="input-field"
          />
        </div>

        <div>
          <label className="text-sm text-morandi-500 block mb-1">補登原因（必填）</label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={3}
            placeholder="例：當天太忙忘記打下班卡"
            className="input-field"
            maxLength={500}
          />
        </div>

        {err && (
          <div className="text-sm text-rose-500 bg-rose-50 px-3 py-2 rounded-lg">{err}</div>
        )}

        <div className="text-xs text-morandi-400 bg-morandi-50 rounded-lg px-3 py-2">
          📋 提交後狀態為「待簽核」，主管核准後才會列入正式出勤
        </div>

        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 bg-morandi-100 hover:bg-morandi-200 text-morandi-600 rounded-btn text-sm"
          >取消</button>
          <button
            onClick={submit}
            disabled={submitting}
            className="flex-1 py-2.5 bg-earth-400 hover:bg-earth-500 disabled:opacity-50 text-white rounded-btn text-sm"
          >
            {submitting ? '送出中...' : '送出申請'}
          </button>
        </div>
      </div>
    </div>
  );
}
